import React, { ChangeEvent, FocusEvent, useState } from "react";

interface TextInputProps {
  id: string;
  label?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  type?: "text" | "password" | "email" | "tel" | "url";
  disabled?: boolean;
  required?: boolean;
  error?: string;
  maxLength?: number;
  multiline?: boolean;
  rows?: number;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const TextInput: React.FC<TextInputProps> = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  onFocus,
  onBlur,
  type = "text",
  disabled = false,
  required = false,
  error,
  maxLength,
  multiline = false,
  rows = 3,
  className = "",
  size = "md"
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const getSizeClasses = () => {
    switch (size) {
      case "sm":
        return "px-3 py-2 text-sm";
      case "lg":
        return "px-4 py-3 text-lg";
      default:
        return "px-3 py-2.5 text-base";
    }
  };

  const baseClasses = `
    w-full
    border border-secondary-300
    rounded-lg
    bg-white
    transition-all duration-200
    focus:outline-none
    focus:ring-2
    focus:ring-primary-500
    focus:border-primary-500
    placeholder-secondary-400
    ${getSizeClasses()}
    ${disabled ? "bg-secondary-100 text-secondary-500 cursor-not-allowed" : ""}
    ${error ? "border-error-500 focus:ring-error-500 focus:border-error-500" : ""}
    ${isFocused ? "ring-2 ring-primary-500 border-primary-500" : ""}
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  const inputProps = {
    id,
    value,
    onChange: handleChange,
    onFocus: handleFocus,
    onBlur: handleBlur,
    placeholder,
    disabled,
    maxLength,
    required,
    className: baseClasses
  };

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-secondary-700 mb-1">
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      {multiline ? (
        <textarea {...inputProps} rows={rows} style={{ resize: "vertical" }} />
      ) : (
        <input {...inputProps} type={type} />
      )}

      {error && <p className="mt-1 text-sm text-error-600">{error}</p>}
    </div>
  );
};

export { TextInput };
